import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileSpreadsheet, FileText, TrendingUp, TrendingDown, Printer, Wallet, Receipt, Scale, MessageCircle, Plus, Trash2, Loader2, BookOpen, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/api";
import { rupiah, MONTHS, formatDate } from "@/lib/format";
import { waLink, waMessages } from "@/lib/whatsapp";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const now = new Date();
const YEARS = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

const LEDGER_SECTIONS = {
  laba_rugi: [["pendapatan", "Pendapatan"], ["beban", "Beban"]],
  neraca: [["aset", "Aset"], ["liabilitas", "Liabilitas"], ["ekuitas", "Ekuitas"]],
};

function LedgerItemsCard({ statement, onChanged }) {
  const sections = LEDGER_SECTIONS[statement];
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ section: sections[0][0], name: "", amount: "", cash_account: "Bank" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/ledger", { params: { statement } }).then((res) => setItems(res.data)).catch(() => {});
  useEffect(() => { load(); }, [statement]);

  const needsCashAccount = statement === "neraca" && (form.section === "liabilitas" || form.section === "ekuitas");

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/ledger", { ...form, statement, amount: Number(form.amount) || 0,
        cash_account: needsCashAccount ? form.cash_account : null });
      toast.success("Item ledger ditambahkan");
      setForm({ section: sections[0][0], name: "", amount: "", cash_account: "Bank" });
      load();
      onChanged?.();
    } catch (err) {
      toast.error(apiError(err, "Gagal menambah item ledger"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Hapus item "${item.name}"?`)) return;
    try {
      await api.delete(`/ledger/${item.id}`);
      toast.success("Item dihapus");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(apiError(err, "Gagal menghapus item"));
    }
  };

  return (
    <Card className="border-border shadow-sm" data-testid={`ledger-card-${statement}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <BookOpen className="h-5 w-5 text-primary" />
          Item Ledger Tambahan — {statement === "neraca" ? "Neraca" : "Laba Rugi"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {statement === "neraca"
            ? "Tambahkan pos manual ke neraca, misal aset (peralatan musik), liabilitas (utang), atau ekuitas (modal awal). Laba Ditahan dihitung otomatis sebagai penyeimbang."
            : "Tambahkan pos manual ke laba rugi, misal pendapatan sewa studio atau beban di luar pencatatan rutin. Item ini langsung memengaruhi total pemasukan/pengeluaran."}
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[160px_1fr_160px_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label className="text-xs">Kelompok</Label>
            <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}>
              <SelectTrigger data-testid={`ledger-section-select-${statement}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {sections.map(([val, label]) => <SelectItem key={val} value={val}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Nama Item</Label>
            <Input placeholder={statement === "neraca" ? "cth: Peralatan musik / Utang bank / Modal awal" : "cth: Pendapatan sewa studio"}
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`ledger-name-input-${statement}`} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Jumlah (Rp)</Label>
            <Input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid={`ledger-amount-input-${statement}`} />
          </div>
          <Button onClick={save} disabled={saving || !form.name || !form.amount} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`ledger-add-button-${statement}`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Tambah
          </Button>
        </div>
        {needsCashAccount && (
          <div className="space-y-1.5">
            <Label className="text-xs">Dana diterima ke Akun</Label>
            <Select value={form.cash_account} onValueChange={(v) => setForm({ ...form, cash_account: v })}>
              <SelectTrigger className="sm:w-64" data-testid="ledger-cash-account-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Kas">Kas</SelectItem>
                <SelectItem value="Bank">Bank</SelectItem>
                <SelectItem value="Kas Kecil">Kas Kecil</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Utang/modal menambah saldo akun ini, sehingga saldo akun tidak minus dan penjumlahan neraca tepat.
            </p>
          </div>
        )}
        {items.length > 0 && (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {items.map((i) => (
              <li key={i.id} className="flex items-center justify-between px-4 py-2.5 transition-colors duration-200 hover:bg-slate-50" data-testid={`ledger-item-${i.id}`}>
                <div className="flex items-center gap-3">
                  <Badge variant="secondary">{sections.find(([v]) => v === i.section)?.[1] || i.section}</Badge>
                  <span className="text-sm font-medium">{i.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-primary">{rupiah(i.amount)}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(i)} data-testid={`ledger-delete-${i.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function MonthYearPicker({ month, year, setMonth, setYear, prefix }) {
  return (
    <div className="flex gap-2">
      <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
        <SelectTrigger className="w-36" data-testid={`${prefix}-month-select`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
        <SelectTrigger className="w-28" data-testid={`${prefix}-year-select`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function Reports() {
  const [fromM, setFromM] = useState(now.getMonth() + 1);
  const [fromY, setFromY] = useState(now.getFullYear());
  const [toM, setToM] = useState(now.getMonth() + 1);
  const [toY, setToY] = useState(now.getFullYear());
  const [finance, setFinance] = useState(null);

  const [sppM, setSppM] = useState(now.getMonth() + 1);
  const [sppY, setSppY] = useState(now.getFullYear());
  const [spp, setSpp] = useState(null);

  const [neracaM, setNeracaM] = useState(now.getMonth() + 1);
  const [neracaY, setNeracaY] = useState(now.getFullYear());
  const [neraca, setNeraca] = useState(null);

  const start = `${fromY}-${String(fromM).padStart(2, "0")}-01`;
  const endDay = new Date(toY, toM, 0).getDate();
  const end = `${toY}-${String(toM).padStart(2, "0")}-${endDay}`;

  const loadFinance = () => {
    setFinance(null);
    api.get("/reports/finance", { params: { start, end } }).then((res) => setFinance(res.data)).catch(() => {});
  };
  useEffect(() => { loadFinance(); }, [fromM, fromY, toM, toY]);

  useEffect(() => {
    setSpp(null);
    api.get("/reports/spp", { params: { month: sppM, year: sppY } }).then((res) => setSpp(res.data)).catch(() => {});
  }, [sppM, sppY]);

  const neracaEndDay = new Date(neracaY, neracaM, 0).getDate();
  const neracaEnd = `${neracaY}-${String(neracaM).padStart(2, "0")}-${neracaEndDay}`;

  const loadNeraca = () => {
    setNeraca(null);
    api.get("/reports/neraca", { params: { end: neracaEnd } }).then((res) => setNeraca(res.data)).catch(() => {});
  };
  useEffect(() => { loadNeraca(); }, [neracaM, neracaY]);

  const [guruM, setGuruM] = useState(now.getMonth() + 1);
  const [guruY, setGuruY] = useState(now.getFullYear());
  const [guru, setGuru] = useState(null);
  useEffect(() => {
    setGuru(null);
    api.get("/reports/class-meetings", { params: { month: guruM, year: guruY } }).then((res) => setGuru(res.data)).catch(() => {});
  }, [guruM, guruY]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Laporan Keuangan</h1>
        <p className="mt-1 text-sm text-muted-foreground">Laba rugi, status SPP siswa, dan ekspor laporan.</p>
      </div>

      <Tabs defaultValue="laba-rugi">
        <TabsList>
          <TabsTrigger value="laba-rugi" data-testid="tab-laba-rugi">Laba Rugi</TabsTrigger>
          <TabsTrigger value="spp" data-testid="tab-spp">Status SPP Siswa</TabsTrigger>
          <TabsTrigger value="neraca" data-testid="tab-neraca">Neraca</TabsTrigger>
          <TabsTrigger value="kehadiran-guru" data-testid="tab-kehadiran-guru">Pertemuan Guru</TabsTrigger>
        </TabsList>

        <TabsContent value="laba-rugi" className="mt-6 space-y-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Dari</p>
                <MonthYearPicker month={fromM} year={fromY} setMonth={setFromM} setYear={setFromY} prefix="report-from" />
              </div>
              <div>
                <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Sampai</p>
                <MonthYearPicker month={toM} year={toY} setMonth={setToM} setYear={setToY} prefix="report-to" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="gap-2" onClick={() => window.open(`${API}/reports/export/excel?start=${start}&end=${end}`, "_blank")} data-testid="export-excel-button">
                <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Export Excel
              </Button>
              <Button variant="outline" className="gap-2" onClick={() => window.open(`${API}/reports/export/pdf?start=${start}&end=${end}`, "_blank")} data-testid="export-pdf-button">
                <FileText className="h-4 w-4 text-destructive" /> Export PDF
              </Button>
            </div>
          </div>

          {!finance ? (
            <p className="py-16 text-center text-muted-foreground" data-testid="finance-loading">Memuat laporan...</p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Card className="border-border shadow-sm" data-testid="report-total-income">
                  <CardContent className="flex items-center gap-4 p-5">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Wallet className="h-5 w-5" /></div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Total Pemasukan</p>
                      <p className="font-heading text-lg font-bold text-primary">{rupiah(finance.total_income)}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border shadow-sm" data-testid="report-total-expense">
                  <CardContent className="flex items-center gap-4 p-5">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent"><Receipt className="h-5 w-5" /></div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Total Pengeluaran</p>
                      <p className="font-heading text-lg font-bold text-accent">{rupiah(finance.total_expense)}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border shadow-sm" data-testid="report-profit">
                  <CardContent className="flex items-center gap-4 p-5">
                    <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${finance.profit >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-destructive/10 text-destructive"}`}>
                      {finance.profit >= 0 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Laba / (Rugi)</p>
                      <p className={`font-heading text-lg font-bold ${finance.profit >= 0 ? "text-emerald-600" : "text-destructive"}`}>{rupiah(finance.profit)}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-border shadow-sm" data-testid="report-chart">
                <CardHeader>
                  <CardTitle className="text-lg">Perbandingan per Bulan</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={finance.by_month}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000000).toFixed(1)}jt`} />
                      <Tooltip formatter={(v) => rupiah(v)} />
                      <Legend />
                      <Bar dataKey="pemasukan" name="Pemasukan" fill="#1E3A8A" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="pengeluaran" name="Pengeluaran" fill="#F97316" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="laba" name="Laba" fill="#10B981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Card className="border-border shadow-sm" data-testid="report-income-table">
                  <CardHeader><CardTitle className="text-lg">Rincian Pemasukan</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tanggal</TableHead>
                          <TableHead>Siswa</TableHead>
                          <TableHead>Keterangan</TableHead>
                          <TableHead className="text-right">Jumlah</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {finance.payments.length === 0 ? (
                          <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Tidak ada pemasukan.</TableCell></TableRow>
                        ) : finance.payments.map((p) => (
                          <TableRow key={p.id} className="hover:bg-slate-50">
                            <TableCell>{formatDate(p.paid_at)}</TableCell>
                            <TableCell className="font-medium">{p.student_name}</TableCell>
                            <TableCell>{p.payment_type === "SPP" ? `SPP ${p.month_name} ${p.year}` : `${p.payment_type}${p.description ? ` — ${p.description}` : ""}`}</TableCell>
                            <TableCell className="text-right font-semibold text-primary">{rupiah(p.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card className="border-border shadow-sm" data-testid="report-expense-table">
                  <CardHeader className="flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-lg">Rincian Pengeluaran</CardTitle>
                    <div className="flex flex-wrap gap-1.5">
                      {finance.by_category.map((c) => (
                        <Badge key={c.category} variant="secondary" className="text-xs">{c.category}: {rupiah(c.total)}</Badge>
                      ))}
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tanggal</TableHead>
                          <TableHead>Kategori</TableHead>
                          <TableHead>Deskripsi</TableHead>
                          <TableHead className="text-right">Jumlah</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {finance.expenses.length === 0 ? (
                          <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Tidak ada pengeluaran.</TableCell></TableRow>
                        ) : finance.expenses.map((e) => (
                          <TableRow key={e.id} className="hover:bg-slate-50">
                            <TableCell>{formatDate(e.expense_date)}</TableCell>
                            <TableCell>{e.category}</TableCell>
                            <TableCell>{e.description}</TableCell>
                            <TableCell className="text-right font-semibold text-accent">{rupiah(e.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              {(finance.journal_income > 0 || finance.journal_expense > 0) && (
                <Card className="border-border shadow-sm" data-testid="journal-postings-card">
                  <CardHeader><CardTitle className="text-lg">Posting dari Jurnal Manual</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tanggal</TableHead>
                          <TableHead>Deskripsi</TableHead>
                          <TableHead>Akun</TableHead>
                          <TableHead>Sisi</TableHead>
                          <TableHead className="text-right">Jumlah</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(finance.journal_income_rows || []).map((r, i) => (
                          <TableRow key={`ji-${i}`} className="hover:bg-slate-50" data-testid={`journal-income-row-${i}`}>
                            <TableCell>{formatDate(r.date)}</TableCell>
                            <TableCell>{r.description}</TableCell>
                            <TableCell>{r.account}</TableCell>
                            <TableCell><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Pendapatan</Badge></TableCell>
                            <TableCell className="text-right font-semibold text-primary">{rupiah(r.amount)}</TableCell>
                          </TableRow>
                        ))}
                        {(finance.journal_expense_rows || []).map((r, i) => (
                          <TableRow key={`je-${i}`} className="hover:bg-slate-50" data-testid={`journal-expense-row-${i}`}>
                            <TableCell>{formatDate(r.date)}</TableCell>
                            <TableCell>{r.description}</TableCell>
                            <TableCell>{r.account}</TableCell>
                            <TableCell><Badge className="bg-accent/10 text-accent hover:bg-accent/10">Beban</Badge></TableCell>
                            <TableCell className="text-right font-semibold text-accent">{rupiah(r.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              <LedgerItemsCard statement="laba_rugi" onChanged={loadFinance} />
            </>
          )}
        </TabsContent>

        <TabsContent value="spp" className="mt-6 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <MonthYearPicker month={sppM} year={sppY} setMonth={setSppM} setYear={setSppY} prefix="spp-report" />
            {spp && (
              <div className="flex flex-wrap gap-2" data-testid="spp-report-summary">
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Lunas: {spp.paid_count} siswa</Badge>
                <Badge className="bg-accent/10 text-accent hover:bg-accent/10">Belum Bayar: {spp.unpaid_count} siswa</Badge>
                <Badge variant="secondary">Terkumpul: {rupiah(spp.collected)}</Badge>
              </div>
            )}
          </div>

          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">
                Status Pembayaran SPP — {spp ? `${spp.month_name} ${spp.year}` : ""}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Siswa</TableHead>
                    <TableHead>Kelas</TableHead>
                    <TableHead className="text-right">Tagihan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tanggal Bayar</TableHead>
                    <TableHead>Kwitansi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!spp ? (
                    <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">Memuat data...</TableCell></TableRow>
                  ) : spp.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground" data-testid="spp-report-empty">Belum ada siswa aktif.</TableCell></TableRow>
                  ) : spp.rows.map((r) => (
                    <TableRow key={r.student_id} className="hover:bg-slate-50" data-testid={`spp-row-${r.student_id}`}>
                      <TableCell className="font-semibold">{r.name}</TableCell>
                      <TableCell>{r.class_name}</TableCell>
                      <TableCell className="text-right font-medium">{rupiah(r.monthly_fee)}</TableCell>
                      <TableCell>
                        <Badge className={r.status === "Lunas" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-accent/10 text-accent hover:bg-accent/10"}>
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{r.paid_at ? formatDate(r.paid_at) : "-"}</TableCell>
                      <TableCell>
                        {r.payment_id ? (
                          <Link to={`/kwitansi/${r.payment_id}`} target="_blank">
                            <Button variant="ghost" size="sm" className="gap-1.5 text-primary" data-testid={`spp-receipt-${r.student_id}`}>
                              <Printer className="h-3.5 w-3.5" /> {r.receipt_no}
                            </Button>
                          </Link>
                        ) : r.phone ? (
                          <a
                            href={waLink(r.phone, waMessages.reminder({ parentName: r.parent_name, studentName: r.name, className: r.class_name, grade: r.grade, monthName: spp.month_name, year: spp.year, amount: r.monthly_fee }))}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`wa-remind-${r.student_id}`}
                          >
                            <Button variant="ghost" size="sm" className="gap-1.5 text-emerald-600">
                              <MessageCircle className="h-3.5 w-3.5" /> Ingatkan
                            </Button>
                          </a>
                        ) : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="neraca" className="mt-6 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Neraca Per Akhir Bulan</p>
              <MonthYearPicker month={neracaM} year={neracaY} setMonth={setNeracaM} setYear={setNeracaY} prefix="neraca" />
            </div>
          </div>

          {!neraca ? (
            <p className="py-16 text-center text-muted-foreground" data-testid="neraca-loading">Memuat neraca...</p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Card className="border-border shadow-sm" data-testid="neraca-aset">
                  <CardHeader><CardTitle className="text-lg">Aset</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableBody>
                        <TableRow className="hover:bg-slate-50">
                          <TableCell>
                            Kas
                            <span className="block text-xs text-muted-foreground">Pembayaran tunai dikurangi pengeluaran kas</span>
                          </TableCell>
                          <TableCell className="text-right font-semibold" data-testid="neraca-kas">{rupiah(neraca.kas)}</TableCell>
                        </TableRow>
                        <TableRow className="hover:bg-slate-50">
                          <TableCell>
                            Bank
                            <span className="block text-xs text-muted-foreground">Pembayaran transfer dikurangi pengeluaran bank</span>
                          </TableCell>
                          <TableCell className="text-right font-semibold" data-testid="neraca-bank">{rupiah(neraca.bank)}</TableCell>
                        </TableRow>
                        <TableRow className="hover:bg-slate-50">
                          <TableCell>
                            Kas Kecil
                            <span className="block text-xs text-muted-foreground">Dana operasional harian (petty cash)</span>
                          </TableCell>
                          <TableCell className="text-right font-semibold" data-testid="neraca-kas-kecil">{rupiah(neraca.kas_kecil)}</TableCell>
                        </TableRow>
                        <TableRow className="hover:bg-slate-50">
                          <TableCell>
                            Piutang SPP
                            <span className="block text-xs text-muted-foreground">Tagihan SPP siswa aktif yang belum dibayar</span>
                          </TableCell>
                          <TableCell className="text-right font-semibold" data-testid="neraca-piutang">{rupiah(neraca.piutang_total)}</TableCell>
                        </TableRow>
                        {(neraca.aset_items || []).map((i) => (
                          <TableRow key={i.id} className="hover:bg-slate-50" data-testid={`neraca-aset-item-${i.id}`}>
                            <TableCell>
                              {i.name}
                              <span className="block text-xs text-muted-foreground">Item manual</span>
                            </TableCell>
                            <TableCell className="text-right font-semibold">{rupiah(i.amount)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-primary/5 font-bold">
                          <TableCell className="font-bold text-primary">Total Aset</TableCell>
                          <TableCell className="text-right font-bold text-primary" data-testid="neraca-total-aset">{rupiah(neraca.total_aset)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card className="border-border shadow-sm" data-testid="neraca-pasiva">
                  <CardHeader><CardTitle className="text-lg">Liabilitas & Ekuitas</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableBody>
                        {(neraca.liabilitas_items || []).length === 0 ? (
                          <TableRow className="hover:bg-slate-50">
                            <TableCell>
                              Liabilitas / Utang
                              <span className="block text-xs text-muted-foreground">Belum ada liabilitas tercatat</span>
                            </TableCell>
                            <TableCell className="text-right font-semibold" data-testid="neraca-liabilitas">{rupiah(0)}</TableCell>
                          </TableRow>
                        ) : (neraca.liabilitas_items || []).map((i) => (
                          <TableRow key={i.id} className="hover:bg-slate-50" data-testid={`neraca-liabilitas-item-${i.id}`}>
                            <TableCell>
                              {i.name}
                              <span className="block text-xs text-muted-foreground">Liabilitas (item manual)</span>
                            </TableCell>
                            <TableCell className="text-right font-semibold">{rupiah(i.amount)}</TableCell>
                          </TableRow>
                        ))}
                        {(neraca.ekuitas_items || []).map((i) => (
                          <TableRow key={i.id} className="hover:bg-slate-50" data-testid={`neraca-ekuitas-item-${i.id}`}>
                            <TableCell>
                              {i.name}
                              <span className="block text-xs text-muted-foreground">Ekuitas (item manual)</span>
                            </TableCell>
                            <TableCell className="text-right font-semibold">{rupiah(i.amount)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="hover:bg-slate-50">
                          <TableCell>
                            Ekuitas — Laba Ditahan
                            <span className="block text-xs text-muted-foreground">Akumulasi laba sejak awal beroperasi</span>
                          </TableCell>
                          <TableCell className="text-right font-semibold" data-testid="neraca-ekuitas">{rupiah(neraca.laba_ditahan)}</TableCell>
                        </TableRow>
                        <TableRow className="bg-primary/5">
                          <TableCell className="font-bold text-primary">Total Liabilitas & Ekuitas</TableCell>
                          <TableCell className="text-right font-bold text-primary" data-testid="neraca-total-pasiva">{rupiah(neraca.total_pasiva)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              <LedgerItemsCard statement="neraca" onChanged={loadNeraca} />

              <Card className="border-border shadow-sm" data-testid="neraca-piutang-detail">
                <CardHeader><CardTitle className="text-lg">Rincian Piutang SPP</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Siswa</TableHead>
                        <TableHead>Kelas</TableHead>
                        <TableHead>Bulan Menunggak</TableHead>
                        <TableHead className="text-right">Jumlah</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {neraca.piutang_rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="py-8 text-center text-muted-foreground" data-testid="neraca-piutang-empty">
                            Tidak ada piutang — semua SPP sudah lunas.
                          </TableCell>
                        </TableRow>
                      ) : neraca.piutang_rows.map((r) => (
                        <TableRow key={r.name} className="hover:bg-slate-50" data-testid={`neraca-piutang-${r.name.replace(/\s+/g, "-").toLowerCase()}`}>
                          <TableCell className="font-semibold">{r.name}</TableCell>
                          <TableCell>{r.class_name}</TableCell>
                          <TableCell>
                            <Badge className="bg-accent/10 text-accent hover:bg-accent/10">{r.months} bulan</Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-accent">{rupiah(r.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="kehadiran-guru" className="mt-6 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Rekap jumlah pertemuan setiap guru dengan siswa-siswanya {guru ? `— ${guru.month_name} ${guru.year}` : ""}. Data berasal dari absensi siswa di menu Absensi.
            </p>
            <MonthYearPicker month={guruM} year={guruY} setMonth={setGuruM} setYear={setGuruY} prefix="report-guru" />
          </div>
          {!guru ? (
            <p className="py-10 text-center text-muted-foreground">Memuat data...</p>
          ) : guru.rows.length === 0 ? (
            <p className="py-10 text-center text-muted-foreground" data-testid="guru-recap-empty">Belum ada kelas.</p>
          ) : guru.rows.map((r) => (
            <Card key={r.class_id} className="border-border shadow-sm" data-testid={`guru-recap-${r.class_id}`}>
              <CardHeader>
                <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-lg">
                  <span className="flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5 text-primary" />
                    {r.class_name}
                    <span className="text-sm font-normal text-muted-foreground">{r.instrument}</span>
                  </span>
                  <span className="text-sm font-normal text-muted-foreground">
                    Guru: <span className="font-semibold text-foreground" data-testid={`guru-name-${r.class_id}`}>{r.teacher_name}</span>
                    {" "}• {r.meetings_per_month} pertemuan/bulan
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nama Siswa</TableHead>
                      <TableHead className="text-center">Hadir</TableHead>
                      <TableHead className="text-center">Izin</TableHead>
                      <TableHead className="text-center">Alpa</TableHead>
                      <TableHead className="text-right">Pertemuan Diikuti</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {r.students.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">Belum ada siswa aktif di kelas ini.</TableCell>
                      </TableRow>
                    ) : r.students.map((st) => (
                      <TableRow key={st.name} className="hover:bg-slate-50 dark:hover:bg-white/5" data-testid={`guru-student-${r.class_id}-${st.name.replace(/\s+/g, "-").toLowerCase()}`}>
                        <TableCell className="font-semibold">{st.name}</TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{st.hadir}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{st.izin}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{st.alpa}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          <span className={st.hadir === st.quota && st.quota > 0 ? "text-emerald-600" : "text-foreground"}>{st.hadir}/{st.quota}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
